// Market wire types and message builders. Phase 4 Step 6.
//
// Precedent: workshop-messages.ts. Same shape: discriminated unions over
// the Envelope<T,P> structure inlined here to avoid a circular import,
// one builder per message type, optional payload fields use the
// conditional-spread idiom per DECISIONS 2026-04-17 "Optional object
// fields under exactOptionalPropertyTypes".
//
// Re-exported from shared/messages.ts so consumers can import from
// either file. Routing — Worker → ZoneDO → MarketDO via cross-DO RPC —
// is wired in zone-do.ts at Step 11; this file is shape-only.

import type { ItemId } from "./items.ts";
import type {
  ActiveListing,
  NpcInventory,
  NpcPriceEntry,
  PriceHistoryEntry,
} from "./market.ts";
import type { StationId } from "./stations.ts";

// `ActiveListing` and the other domain shapes are defined in
// shared/market.ts; re-imported here so the message envelope types
// can reference them. The wire-only `Client*` / `Server*` envelopes
// stay in this file (Step 6).

// -----------------------------------------------------------------------
// Envelope (inlined to avoid a circular import with messages.ts)
// -----------------------------------------------------------------------

type Envelope<T extends string, P> = {
  type: T;
  ts: number;
  payload: P;
  // Phase 5 Step 3 — durable wire augmentation. Mirrors messages.ts.
  seq?: number;
  eventId?: string;
};

// -----------------------------------------------------------------------
// Client → Server
// -----------------------------------------------------------------------

// quantity is always 1 in Phase 4 (crafted items list per-stack); the
// field is on the wire so Phase 5+ batching doesn't break the protocol.
export type ClientListItem = Envelope<
  "listItem",
  { itemId: ItemId; price: number; quantity: number }
>;
export type ClientCancelListing = Envelope<"cancelListing", { listingId: string }>;
export type ClientBuyListing = Envelope<"buyListing", { listingId: string }>;
export type ClientNpcSell = Envelope<
  "npcSell",
  { itemId: ItemId; quantity: number }
>;
export type ClientNpcBuy = Envelope<
  "npcBuy",
  { itemId: ItemId; quantity: number }
>;
// Empty payload still wrapped per CONTRACTS §2.
export type ClientGetMarketView = Envelope<"getMarketView", Record<string, never>>;
// sinceTs filters price_history rows by ts >= sinceTs. Absent = full
// window per server-side default. Optional via conditional-spread in
// the builder so JSON.stringify omits the key.
//
// Fix 3 (Phase 4 close): the picker shows crafted base IDs (e.g.
// "copper_wire") but price_history stores quality-suffixed rows
// ("copper_wire:good"), so an exact itemId lookup for a crafted base
// always returned empty. Added `itemIdPrefix` — server does a
// `WHERE item_id LIKE prefix || ':%'` match instead of exact. Exactly
// one of `itemId` / `itemIdPrefix` must be present; server rejects
// both or neither.
export type ClientGetPriceHistory = Envelope<
  "getPriceHistory",
  { itemId?: ItemId; itemIdPrefix?: string; sinceTs?: number }
>;

// Phase 6 Block C: Market Analysis cross-station price query.
// Client sends this when the History tab's station picker selects a remote
// station. Payload mirrors getPriceHistory: exactly one of itemId / itemIdPrefix.
// Server returns remoteMarketView with the remote station's price history.
// Gated by market-analysis Journeyman+ (500 XP) — server checks before processing.
export type ClientRequestRemoteMarketView = Envelope<
  "requestRemoteMarketView",
  { stationId: StationId; itemId?: ItemId; itemIdPrefix?: string; sinceTs?: number }
>;

export type ClientMarketMessage =
  | ClientListItem
  | ClientCancelListing
  | ClientBuyListing
  | ClientNpcSell
  | ClientNpcBuy
  | ClientGetMarketView
  | ClientGetPriceHistory
  | ClientRequestRemoteMarketView;

// -----------------------------------------------------------------------
// Server → Client
// -----------------------------------------------------------------------

// Hydrate response for the market panel. Sent on getMarketView replies
// (panel open + poll). `listings` are active-only; `npcPrices` are
// pre-computed by the server per CONTRACTS §8 ("server computes,
// client displays"); `fees` is collected_fees.total at this snapshot.
//
// Fix 4 (Phase 4 close): `credits` is the caller's current balance
// read from their PlayerDO at the same tick. Closes the seller-in-
// session staleness gap documented in zone-market.ts — a silent sale
// on the server would only surface on the next creditsUpdated push
// (which never fires while the seller is connected), so the poll
// response now carries the authoritative balance and the client's
// HUD refreshes off it.
export type ServerMarketView = Envelope<
  "marketView",
  {
    listings: ActiveListing[];
    npcInventory: NpcInventory;
    npcPrices: NpcPriceEntry[];
    fees: number;
    credits: number;
  }
>;

// Personal confirmation to the seller after a successful createListing.
// `credits` is the seller's post-fee balance — Flow A (DECISIONS
// 2026-04-18 "Phase 4 Step 9 listing-fee money flow") debits the fee
// at list time, so this reflects that deduction.
export type ServerListingCreated = Envelope<
  "listingCreated",
  { listing: ActiveListing; credits: number }
>;

// Personal confirmation to the seller after cancelListing. The
// refunded item is one stack of count 1 in Phase 4 (per the listItem
// quantity=1 invariant); the shape carries count for forward
// compatibility with multi-quantity listings.
export type ServerListingCancelled = Envelope<
  "listingCancelled",
  {
    listingId: string;
    refundedItem: { itemId: ItemId; count: number };
    credits: number;
  }
>;

// Personal notification routed to the seller when their listing sells.
// Buyer's side reconciles via inventoryUpdated + creditsUpdated; this
// envelope is seller-only. `fee` is informational — under Flow A the
// fee was already retained at list time and the seller receives the
// FULL price here. We surface the original fee anyway so the seller's
// UX can show "your listing netted X (Y fee was retained at list)".
// `itemId` lets the seller's toast copy read "Listing sold: <itemId>
// for <price>c" without a separate inventory lookup (Phase 5 Step 21
// followup — applyListingSold wiring).
export type ServerListingSold = Envelope<
  "listingSold",
  {
    listingId: string;
    itemId: ItemId;
    buyer: string;
    seller: string;
    price: number;
    fee: number;
  }
>;

// Catch-all credits delta. `reason` is the same string passed to
// addCredits / spendCredits on the PlayerDO side; client uses it for
// toast text ("Listing sold: +84c", "NPC purchase: -50c").
export type ServerCreditsUpdated = Envelope<
  "creditsUpdated",
  { credits: number; reason: string }
>;

// Reply to getPriceHistory. `entries` is ordered ts DESC by the server
// (most recent first); client renders the sparkline in whichever
// direction it wants.
export type ServerPriceHistory = Envelope<
  "priceHistory",
  { itemId: ItemId; entries: PriceHistoryEntry[] }
>;

// Phase 6 Block C: Market Analysis cross-station price history response.
// Ephemeral (direct-send): remote query results don't need replay.
// itemKey echoes the itemId or itemIdPrefix the client requested so it can
// key `remoteHistories[stationId][itemKey]` without re-parsing the request.
export type ServerRemoteMarketView = Envelope<
  "remoteMarketView",
  { stationId: StationId; itemKey: string; entries: PriceHistoryEntry[] }
>;

export type ServerMarketMessage =
  | ServerMarketView
  | ServerListingCreated
  | ServerListingCancelled
  | ServerListingSold
  | ServerCreditsUpdated
  | ServerPriceHistory
  | ServerRemoteMarketView;

// -----------------------------------------------------------------------
// Builders — one per message type. Names match the contracts §2 regex
// /^(build|make|create).*Message$/.
// -----------------------------------------------------------------------

export const buildListItemMessage = (
  itemId: ItemId,
  price: number,
  quantity: number,
): ClientListItem => ({
  type: "listItem",
  ts: Date.now(),
  payload: { itemId, price, quantity },
});

export const buildCancelListingMessage = (listingId: string): ClientCancelListing => ({
  type: "cancelListing",
  ts: Date.now(),
  payload: { listingId },
});

export const buildBuyListingMessage = (listingId: string): ClientBuyListing => ({
  type: "buyListing",
  ts: Date.now(),
  payload: { listingId },
});

export const buildNpcSellMessage = (itemId: ItemId, quantity: number): ClientNpcSell => ({
  type: "npcSell",
  ts: Date.now(),
  payload: { itemId, quantity },
});

export const buildNpcBuyMessage = (itemId: ItemId, quantity: number): ClientNpcBuy => ({
  type: "npcBuy",
  ts: Date.now(),
  payload: { itemId, quantity },
});

export const buildGetMarketViewMessage = (): ClientGetMarketView => ({
  type: "getMarketView",
  ts: Date.now(),
  payload: {},
});

export const buildGetPriceHistoryMessage = (
  itemId: ItemId,
  sinceTs?: number,
): ClientGetPriceHistory => ({
  type: "getPriceHistory",
  ts: Date.now(),
  payload: { itemId, ...(sinceTs !== undefined && { sinceTs }) },
});

// Fix 3: prefix-mode builder for crafted-base picker lookups. Server
// returns history rows whose item_id starts with `${prefix}:`, i.e.
// all quality tiers of that crafted base. Response still comes back
// as `priceHistory { itemId, entries }` where `itemId` echoes the
// prefix (the key the client asked for) — this matches the
// `priceHistories[selectedItemId]` lookup already used by the tab.
export const buildGetPriceHistoryPrefixMessage = (
  itemIdPrefix: string,
  sinceTs?: number,
): ClientGetPriceHistory => ({
  type: "getPriceHistory",
  ts: Date.now(),
  payload: { itemIdPrefix, ...(sinceTs !== undefined && { sinceTs }) },
});

export const buildMarketViewMessage = (
  listings: ActiveListing[],
  npcInventory: NpcInventory,
  npcPrices: NpcPriceEntry[],
  fees: number,
  credits: number,
): ServerMarketView => ({
  type: "marketView",
  ts: Date.now(),
  payload: { listings, npcInventory, npcPrices, fees, credits },
});

export const buildListingCreatedMessage = (
  listing: ActiveListing,
  credits: number,
): ServerListingCreated => ({
  type: "listingCreated",
  ts: Date.now(),
  payload: { listing, credits },
});

export const buildListingCancelledMessage = (
  listingId: string,
  refundedItem: { itemId: ItemId; count: number },
  credits: number,
): ServerListingCancelled => ({
  type: "listingCancelled",
  ts: Date.now(),
  payload: { listingId, refundedItem, credits },
});

export const buildListingSoldMessage = (
  listingId: string,
  itemId: ItemId,
  buyer: string,
  seller: string,
  price: number,
  fee: number,
): ServerListingSold => ({
  type: "listingSold",
  ts: Date.now(),
  payload: { listingId, itemId, buyer, seller, price, fee },
});

export const buildCreditsUpdatedMessage = (
  credits: number,
  reason: string,
): ServerCreditsUpdated => ({
  type: "creditsUpdated",
  ts: Date.now(),
  payload: { credits, reason },
});

export const buildPriceHistoryMessage = (
  itemId: ItemId,
  entries: PriceHistoryEntry[],
): ServerPriceHistory => ({
  type: "priceHistory",
  ts: Date.now(),
  payload: { itemId, entries },
});

export const buildRequestRemoteMarketViewMessage = (
  stationId: StationId,
  itemId?: ItemId,
  itemIdPrefix?: string,
  sinceTs?: number,
): ClientRequestRemoteMarketView => ({
  type: "requestRemoteMarketView",
  ts: Date.now(),
  payload: {
    stationId,
    ...(itemId !== undefined && { itemId }),
    ...(itemIdPrefix !== undefined && { itemIdPrefix }),
    ...(sinceTs !== undefined && { sinceTs }),
  },
});

export const buildRemoteMarketViewMessage = (
  stationId: StationId,
  itemKey: string,
  entries: PriceHistoryEntry[],
): ServerRemoteMarketView => ({
  type: "remoteMarketView",
  ts: Date.now(),
  payload: { stationId, itemKey, entries },
});
